import { EditorState } from 'hubspot-prosemirror/pm/state';

import { FONT_SIZES, FONT_STYLES } from '../constants/fontConstants';
import { EditorConfig } from '../editorCapabilities';
import { isMarkActive } from './markUtils';

export type DefaultFontInfo = Pick<
  NonNullable<EditorConfig['GLOBAL_VARS']>,
  | 'defaultFontSize'
  | 'defaultFontStyle'
  | 'defaultTextColor'
  | 'usePlaintextMode'
>;
export const getDefaultFontProps = (
  editorConfig: EditorConfig
): DefaultFontInfo => {
  const {
    defaultFontSize,
    defaultFontStyle,
    defaultTextColor,
    usePlaintextMode,
  } = editorConfig.GLOBAL_VARS || {};

  const parsedSize = defaultFontSize && parseAvailableFontSize(defaultFontSize);
  return {
    defaultFontSize: (parsedSize && `${parsedSize}pt`) || defaultFontSize,
    defaultFontStyle:
      (defaultFontStyle && parseAvailableFont(defaultFontStyle)) ||
      defaultFontStyle,
    defaultTextColor,
    usePlaintextMode,
  };
};

export const getDefaultFontPropsWithFallback = (
  editorConfig: EditorConfig
): DefaultFontInfo => {
  const { defaultFontSize, defaultFontStyle, defaultTextColor } =
    getDefaultFontProps(editorConfig);
  return {
    defaultFontSize: defaultFontSize || '11pt',
    defaultFontStyle: defaultFontStyle || '',
    defaultTextColor: defaultTextColor || '#444444',
  };
};

export const createDefaultFontStyleString = ({
  defaultFontStyle,
  defaultTextColor,
}: DefaultFontInfo) => {
  let style = '';
  style += defaultFontStyle ? `font-family: ${defaultFontStyle};` : '';
  style += defaultTextColor ? `color: ${defaultTextColor};` : '';
  return style;
};
export const getDefaultEditorStyle = (editorConfig: EditorConfig) => {
  let otherStyles = 'white-space: pre-wrap;';
  const fontInfo = getDefaultFontPropsWithFallback(editorConfig);

  otherStyles +=
    editorConfig.GLOBAL_VARS && editorConfig.GLOBAL_VARS.defaultFontSize
      ? ''
      : 'font-size: 11pt;';
  return otherStyles + createDefaultFontStyleString(fontInfo);
};

export function hasTextColor(state: EditorState) {
  return isMarkActive(state, state.schema.marks.text_color);
}

export function hasFontStyle(state: EditorState) {
  return isMarkActive(state, state.schema.marks.font_style);
}

export function hasFontSize(state: EditorState) {
  return isMarkActive(state, state.schema.marks.font_size);
}

export function parseAvailableFont(fontStyle: string) {
  const foundStyle = FONT_STYLES.find(
    (style) => style.fontKey === fontStyle || style.value === fontStyle
  );
  return foundStyle && foundStyle.value;
}

export function convertPxStrToPtStr(pxValue: string) {
  const pixelValue = parseFloat(pxValue.replace('px', ''));
  const ptValue = pixelValue * 0.75;

  const rounded = Math.round(ptValue);
  const diff = Math.abs(ptValue - rounded);

  // If very close to a whole number (within 0.01), use that points value
  return diff < 0.01 ? `${rounded}pt` : `${ptValue}pt`;
}

export function parseAvailableFontSize(fontSize: string | number) {
  const sizeStr = `${fontSize}`.replace('pt', '');
  const foundSize = FONT_SIZES.find(
    (size) => size.value === sizeStr || size.text === sizeStr
  );
  return foundSize && foundSize.points;
}
